import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { buildSelector, describe, matchCount } from './selector';
import './feature-tour.css';

/**
 * ElementPicker — "Sayfada seç": URL'de ?tourPick=1 varken etkinleşir. Fareyle
 * gezilen öğe vurgulanır, tıklanınca seçici üretilip bulunduğu pencereye
 * (iframe'deyse üst pencereye) postMessage ile gönderilir.
 *   ↑ / ↓  : üst / alt öğeye geç      Enter : onayla      Esc : vazgeç
 *   Alt basılıyken tıklama sayfaya gider (menü / panel açmak için).
 * Yönetim sayfasındaki rehber editörü bu sayfayı iframe içinde açar (PickerFrame).
 */

export const PICK_MESSAGE = 'aycrm:tourPick';

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

const rectOf = (el: Element): Box => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

const ElementPicker: React.FC = () => {
  const location = useLocation();
  const active = new URLSearchParams(location.search).get('tourPick') === '1';
  const [hover, setHover] = useState<Element | null>(null);
  const [picked, setPicked] = useState<Element | null>(null);
  const [selector, setSelector] = useState('');
  const altRef = useRef(false);

  const isOwn = (el: Element | null) => !!el && !!el.closest('.ftour-pick-ui');
  // Simge/metin yerine en yakın tıklanabilir öğeyi (düğme, bağlantı, data-tour, form alanı) hedefle
  const pickable = (el: Element | null): Element | null => {
    if (!el) return null;
    const near = el.closest('[data-tour], button, a, [role="button"], input, select, textarea');
    return near && near !== document.body ? near : el;
  };

  const finish = useCallback((sel: string, el: Element | null) => {
    const payload = { type: PICK_MESSAGE, selector: sel, text: el ? describe(el) : '', matches: matchCount(sel) };
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(payload, window.location.origin);
    } else {
      try {
        sessionStorage.setItem('aycrm.tourPick.result', JSON.stringify(payload));
        navigator.clipboard?.writeText(sel).catch(() => {});
      } catch {
        /* sessiz */
      }
      window.history.back();
    }
  }, []);

  const cancel = useCallback(() => {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: PICK_MESSAGE, cancelled: true }, window.location.origin);
    } else {
      window.history.back();
    }
  }, []);

  // Seçim değişince seçici üret
  useEffect(() => {
    if (!picked) return;
    setSelector(buildSelector(picked));
  }, [picked]);

  useEffect(() => {
    if (!active) return;
    const onMove = (e: MouseEvent) => {
      if (picked) return;
      const el = pickable(document.elementFromPoint(e.clientX, e.clientY));
      if (!el || isOwn(el)) return;
      setHover(el);
    };
    // mousedown/pointerdown/pointerup: sayfadaki dinleyicilere (framer onTap dahil) ulaşmasın
    const onDown = (e: MouseEvent | PointerEvent) => {
      if (altRef.current) return; // Alt: sayfaya geçir
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (isOwn(el)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const onClick = (e: MouseEvent) => {
      if (altRef.current) return;
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (isOwn(el)) return;
      e.preventDefault();
      e.stopPropagation();
      const target = pickable(el);
      if (target) setPicked(target);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Alt') altRef.current = true;
      if (e.key === 'Escape') {
        e.preventDefault();
        if (picked) setPicked(null);
        else cancel();
      } else if (e.key === 'Enter' && picked && selector) {
        e.preventDefault();
        finish(selector, picked);
      } else if (e.key === 'ArrowUp' && picked && picked.parentElement && picked.parentElement !== document.body) {
        e.preventDefault();
        setPicked(picked.parentElement);
      } else if (e.key === 'ArrowDown' && picked && picked.firstElementChild) {
        e.preventDefault();
        setPicked(picked.firstElementChild);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt') altRef.current = false;
    };
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointerup', onDown, true);
    document.addEventListener('mouseup', onDown, true);
    document.addEventListener('click', onClick, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKeyUp, true);
    document.body.classList.add('ftour-picking');
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointerup', onDown, true);
      document.removeEventListener('mouseup', onDown, true);
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKeyUp, true);
      document.body.classList.remove('ftour-picking');
    };
  }, [active, picked, selector, finish, cancel]);

  if (!active) return null;
  const current = picked || hover;
  const box = current ? rectOf(current) : null;
  const count = selector ? matchCount(selector) : 0;

  return (
    <>
      {box && (
        <div className={`ftour-pick-box ${picked ? 'picked' : ''}`} style={box}>
          <span className="ftour-pick-label">{current ? describe(current) : ''}</span>
        </div>
      )}
      <div className="ftour-pick-ui ftour-pick-bar">
        <span className="ftour-chip">Öğe seçici</span>
        {!picked ? (
          <span className="ftour-pick-hint">
            Rehberde anlatılacak öğeye <b>tıklayın</b>. Menü/panel açmak için <kbd>Alt</kbd> basılı tutup tıklayın. <kbd>Esc</kbd> vazgeç.
          </span>
        ) : (
          <>
            <code className="ftour-pick-sel" title={selector}>
              {selector}
            </code>
            <span className={`badge ${count === 1 ? 'badge-light-success' : 'badge-light-warning'}`}>{count === 1 ? 'benzersiz' : `${count} eşleşme`}</span>
            <span className="ftour-pick-hint">
              <kbd>↑</kbd> üst öğe · <kbd>↓</kbd> alt öğe · <kbd>Enter</kbd> onayla
            </span>
            <button type="button" className="ftour-btn ghost" onClick={() => setPicked(null)}>
              Yeniden seç
            </button>
            <button type="button" className="ftour-btn primary" onClick={() => finish(selector, picked)}>
              Bu öğeyi kullan
            </button>
          </>
        )}
        <button type="button" className="ftour-skip ms-auto" onClick={cancel}>
          Vazgeç
        </button>
      </div>
    </>
  );
};

export default ElementPicker;
